/**
 * Global command palette (⌘K / Ctrl+K) for fast navigation.
 * Searches across villas, owners, agents, agencies, vendors, clients.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Home, User, Building2, Wrench, Users, MapPin, X, Loader2 } from 'lucide-react';
import { globalSearch } from '../services/api';

const TYPE_META = {
  villa:  { icon: MapPin,    accent: 'text-emerald-600' },
  owner:  { icon: Home,      accent: 'text-blue-600' },
  agency: { icon: Building2, accent: 'text-purple-600' },
  agent:  { icon: User,      accent: 'text-indigo-600' },
  vendor: { icon: Wrench,    accent: 'text-amber-600' },
  client: { icon: Users,     accent: 'text-slate-600' },
};

let debounceTimer = null;

export default function CommandPalette() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);

  // Global keyboard shortcut
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 40);
      setQ('');
      setResults([]);
      setSelectedIdx(0);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    clearTimeout(debounceTimer);
    if (!q.trim() || q.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    debounceTimer = setTimeout(async () => {
      try {
        const res = await globalSearch(q.trim());
        setResults(res.data?.results || []);
        setSelectedIdx(0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 180);
  }, [q]);

  const go = (item) => {
    setOpen(false);
    nav(item.route);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx((i) => Math.min(results.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Enter' && results[selectedIdx]) { e.preventDefault(); go(results[selectedIdx]); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] px-4 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)}>
      <div
        className="w-full max-w-xl bg-white rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 p-3 border-b border-slate-100">
          <Search size={18} className="text-slate-400" />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search villas, owners, agents, agencies, vendors, clients…"
            className="flex-1 outline-none text-sm"
          />
          {loading && <Loader2 size={14} className="animate-spin text-slate-400" />}
          <kbd className="text-[10px] text-slate-400 border border-slate-200 rounded px-1.5 py-0.5 font-mono">ESC</kbd>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {results.length === 0 && q.trim().length >= 2 && !loading && (
            <div className="p-6 text-center text-sm text-slate-500">No matches for "{q}"</div>
          )}
          {results.length === 0 && q.trim().length < 2 && (
            <div className="p-6 text-center text-sm text-slate-400">
              Type at least 2 characters to search. Use <kbd className="text-[10px] border border-slate-200 rounded px-1 font-mono">↑ ↓</kbd> and <kbd className="text-[10px] border border-slate-200 rounded px-1 font-mono">Enter</kbd>.
            </div>
          )}
          {results.map((r, i) => {
            const Meta = TYPE_META[r.type] || TYPE_META.owner;
            const Icon = Meta.icon;
            return (
              <button
                key={`${r.type}-${r.id}`}
                onClick={() => go(r)}
                onMouseEnter={() => setSelectedIdx(i)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition ${
                  i === selectedIdx ? 'bg-[#cfa190]/10' : 'hover:bg-slate-50'
                }`}
              >
                <Icon size={16} className={Meta.accent} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-800 truncate">{r.label}</div>
                  {r.hint && <div className="text-xs text-slate-500 truncate">{r.hint}</div>}
                </div>
                <span className="text-[10px] uppercase tracking-wide text-slate-400">{r.type}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
